import React, { useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import OpportunityListingCard from './OpporunityCard';
import ListingWrapper from './ListingWrapper';
import { useStoreSelector } from '@ui/hooks/useStoreSelector';
import useOpportunitiesStore from '../store/useOpportunitiesStore';
import OpportunityFilters from './OpportunityFilters';
import { cn } from '@ui/atoms/utils/utils';
import { Text } from '@ui/atoms';
import { useSearchParams } from 'next/navigation';
import {
    getAppliedFilters,
    getRegulationTabs,
    isAdminDeal,
    isBondsView,
    isFestiveDeal,
    isTBillsView,
} from '../utils/utils';
import {
    DESKTOP_LIMIT,
    MOBILE_LIMIT,
    OPPORTUNITY_BOND_TYPES,
    OPPORTUNITY_STATUS,
    SDI_TABS,
} from '../utils/constants';
import useSyncFilters from '../hooks/useSyncFilters';
import { RegulationTypes } from '@helpers/enums/regulation-types';
import { motion } from 'framer-motion';
import { useRouter } from 'next/router';
import OpportunitySkeleton from '@components/OpportunityListing/components/LoadersAndEmptyStates/OpportunitySkeleton';
import OpportunityListingEmptyState from '@components/OpportunityListing/components/LoadersAndEmptyStates/OpportunityListingEmptyState';
import { ErrorHandler } from './ErrorHandler';
import {
    isUpcomingOpportunity,
    opportunityClosedStates,
} from '@helpers/constants';
import { getScrollThreshold } from '../utils/helper';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { activeOpportunityListingGaEventsName, opportunitiesGaEventsName } from '@helpers/ga-events-name-enum';
import { triggerCustomGaEvent } from '@helpers/miscelleanous';

const OpportunityListingResults = ({
    showActiveOpportunities = true,
    activities,
    showFestiveDays,
}) => {
    const router = useRouter();
    const { width } = useWindowDimensions();
    const searchParams = useSearchParams();
    const activeTab = searchParams.get('type') || OPPORTUNITY_BOND_TYPES.BOND;
    const isListedSDI = searchParams.get('regulation') === RegulationTypes.SEBI;
    const { updateQueryParams, clearAllFilters } = useSyncFilters();
    const queryParams = Object.fromEntries(searchParams.entries());
    const { count: filterCount } = getAppliedFilters(queryParams);
    const {
        data,
        count,
        isFetching,
        error,
        fetchOpportunities,
        page,
        status,
        stats,
        isAuthorized,
    } = useStoreSelector(useOpportunitiesStore, (state) => ({
        data: state.data.rows,
        count: state.data.count,
        isFetching: state.isFetching,
        error: state.error,
        fetchOpportunities: state.fetchOpportunities,
        page: state.page,
        stats: state.stats,
        status: state.status,
        isAuthorized: state.isAuthorized,
    }));

    const fetchMoreData = async () => {
        try {
            const queryParams = Object.fromEntries(searchParams.entries());
            await fetchOpportunities({
                ...queryParams,
                status,
                page: Number(page) + 1,
                limit: width < 1280 ? MOBILE_LIMIT : DESKTOP_LIMIT,
            });
        } finally {
        }
    };

    const triggerGaEventsForsdiTabs = (value) => {
        let eventName;
    
        const activeEvents = {
            [SDI_TABS.RBI_REGULATED]: opportunitiesGaEventsName.CLICKED_RBI_SDI_TAB_ACTIVE,
            [SDI_TABS.SEBI_REGULATED]: opportunitiesGaEventsName.CLICKED_SEBI_SDI_TAB_ACTIVE,
        };
        
        const closedEvents = {
            [SDI_TABS.RBI_REGULATED]: opportunitiesGaEventsName.CLICKED_RBI_SDI_TAB_RECENTLY_CLOSED,
            [SDI_TABS.SEBI_REGULATED]: opportunitiesGaEventsName.CLICKED_SEBI_SDI_TAB_RECENTLY_CLOSED,
        };
        
        const unauthorizedEvents = {
            [SDI_TABS.RBI_REGULATED]: activeOpportunityListingGaEventsName.CLICKED_ACTIVE_RBI_SDI_TAB,
            [SDI_TABS.SEBI_REGULATED]: activeOpportunityListingGaEventsName.CLICKED_ACTIVE_SEBI_SDI_TAB,
        };
        
        if (isAuthorized) {
            eventName = showActiveOpportunities ? activeEvents[value] : closedEvents[value];
        } else {
            eventName = unauthorizedEvents[value];
        }
        
        if (eventName) {
            triggerCustomGaEvent(eventName);
        }
    }   

            

    const onRegulationTabChange = (regulation, label) => {
        const params = new URLSearchParams(searchParams.toString());
        params.set('regulation', regulation);

        router.push(
            {
                query: Object.fromEntries(params.entries()),
            },
            undefined,
            { shallow: true }
        );
        triggerGaEventsForsdiTabs(label);
    };

    const regulationTabs = useMemo(
        () => getRegulationTabs(stats, status === 'open'),
        [stats, status]
    );
    const interestRecorded = (opportunityId) => {
        return activities?.length
            ? activities?.find((item) => item.opportunityId === opportunityId)
            : null;
    };

    return (
        <ListingWrapper
            className="w-full min-h-screen px-4 py-6 md:px-0 md:py-10 md:pt-6 bg-basicWhite"
            tag="main"
        >
            <div className="flex flex-col gap-6">
                {isBondsView(activeTab) &&
                status !== OPPORTUNITY_STATUS.CLOSED ? (
                    <OpportunityFilters
                        updateQueryParams={updateQueryParams}
                        opportunitiesCount={count}
                        clearAllFilters={clearAllFilters}
                    />
                ) : null}
                {activeTab === OPPORTUNITY_BOND_TYPES.SDIS ||
                activeTab === OPPORTUNITY_BOND_TYPES.UNLISTEDSDI ? (
                    <motion.div
                        key={'listing-tabs'}
                        className={`inline-flex items-start justify-start overflow-hidden md:gap-0 gap-3 mx-auto md:mx-0 w-full`}
                    >
                        {regulationTabs.map((tab) => {
                            const { id, value } = tab;
                            const isSelected =
                                (isListedSDI && id === 'sebi') ||
                                (!isListedSDI && id === 'rbi');

                            return (
                                <li
                                    key={id}
                                    className={cn(
                                        'md:border-2 border w-1/2 md:w-auto justify-center md:justify-start border-gray-200 md:border-transparent list-none cursor-pointer flex items-center py-2 md:py-[10px] px-3 md:bg-transparent bg-basicWhite rounded-md md:rounded-none',
                                        {
                                            'border-primary-70 md:border-b-primary-500 bg-primary-50 md:bg-none ':
                                                isSelected,
                                        }
                                    )}
                                    onClick={() => {
                                        onRegulationTabChange(id, value);
                                    }}
                                >
                                    <Text
                                        content={value}
                                        className={`relative z-10  ${
                                            isSelected
                                                ? 'text-primary-500 p5-medium md:p4-medium'
                                                : ' text-gray-500 p5-regular md:p4-regular'
                                        }`}
                                    />
                                    <div
                                        className={cn(
                                            'relative z-10 ml-2 md:ml-[10px] rounded-full flex w-5 h-5 items-center justify-center border border-primary-70 bg-basicWhite',
                                            {
                                                'bg-primary-70': isSelected,
                                            }
                                        )}
                                    >
                                        <Text
                                            content={
                                                id === 'sebi'
                                                    ? stats?.sebiSDIs
                                                    : stats?.rbiSDIs
                                            }
                                            className="p7-medium text-primary-500"
                                        />
                                    </div>
                                </li>
                            );
                        })}
                    </motion.div>
                ) : null}

                {isFetching && data.length === 0 ? (
                    <div className="grid grid-cols-1 gap-6 md:gap-10 md:grid-cols-2 xl:grid-cols-3">
                        {Array.from({ length: 6 }, (_, i) => i + 1).map(
                            (item) => (
                                <OpportunitySkeleton
                                    key={item}
                                    type={activeTab}
                                />
                            )
                        )}
                    </div>
                ) : !isFetching && data.length === 0 ? (
                    error ? (
                        <ErrorHandler
                            data={data}
                            error={error}
                            loading={isFetching}
                            isFetchingActiveOpportunities={
                                showActiveOpportunities
                            }
                            hasFilters={filterCount > 0}
                        />
                    ) : (
                        <div className="mx-auto mt-2 col-span-full md:mt-4">
                            <OpportunityListingEmptyState
                                isActiveOpportunities={true}
                                hasFilters={filterCount}
                                isListedSDI={isListedSDI}
                                type={activeTab}
                            />
                        </div>
                    )
                ) : (
                    <InfiniteScroll
                        dataLength={data.length}
                        next={fetchMoreData}
                        hasMore={data.length < count}
                        loader={
                            isFetching && (
                                <div className="flex items-center justify-center gap-3 py-3">
                                    <div className={'button-loader-primary '} />
                                    <Text
                                        content={'Loading results'}
                                        className={cn('py-4 text-gray-500')}
                                    />
                                </div>
                            )
                        }
                        endMessage={''}
                        scrollThreshold={getScrollThreshold(
                            isAuthorized,
                            width
                        )}
                        style={{ overflow: 'unset' }}
                    >
                        <div className="grid grid-cols-1 gap-6 md:gap-10 md:grid-cols-2 xl:grid-cols-3">
                            {data.map((opportunity, index) => (
                                <OpportunityListingCard
                                    opportunity={opportunity}
                                    type={activeTab}
                                    key={`${opportunity.id}-${index}`}
                                    interestRecorded={interestRecorded(
                                        opportunity?.id
                                    )}
                                    isFestiveDeal={isFestiveDeal(
                                        isAuthorized,
                                        showFestiveDays,
                                        opportunity,
                                        !isTBillsView(opportunity?.type)
                                    )}
                                    isAdminDeal={isAdminDeal(
                                        isAuthorized,
                                        opportunity
                                    )}
                                    isUpcoming={isUpcomingOpportunity(
                                        opportunity
                                    )}
                                    isClosed={opportunityClosedStates.includes(
                                        opportunity.state
                                    )}
                                    isAuthorized={isAuthorized}
                                />
                            ))}
                        </div>
                        {error ? (
                            <ErrorHandler
                                data={data}
                                error={error}
                                loading={isFetching}
                                isFetchingActiveOpportunities={
                                    showActiveOpportunities
                                }
                                hasFilters={filterCount > 0}
                            />
                        ) : null}
                    </InfiniteScroll>
                )}
            </div>
        </ListingWrapper>
    );
};

export default OpportunityListingResults;
